import type { ActionClient } from "@adea/actions/client";
import type { AnyActionSpec } from "@adea/actions/spec";
import type { StaffSpecs } from "@adea/platform/staff/specs";
import { gateFetch } from "./actions.ts";
import { callLocale } from "./i18n.tsx";
import { lazyClient } from "./lazy-client.ts";
import { getStaffCsrf } from "./staff-session.ts";

/**
 * The staff pages' action client. Staff actions are not company actions: they run on `/api/staff/actions/…`, behind
 * the staff door's own session and CSRF token, with their own gate. The same typed client as the product's, pointed at those routes, so a staff
 * page reads and changes things exactly like any other screen (cache, instant refetch, confirmations). Its specs
 * arrive with the first staff page (`lazy-client.ts`): the product's first load doesn't carry them.
 */
const staffFetch: typeof fetch = (input, init) =>
  gateFetch(
    typeof input === "string" ? input.replace("/api/v1/actions/", "/api/staff/actions/") : input,
    init,
  );

const confirmWith = (name: string, input: unknown, typed?: string): Promise<string> =>
  staffActions.confirm(name as keyof StaffSpecs, input as never, typed);

const loaded: Record<string, AnyActionSpec> = {};
let arrival: Promise<void> | undefined;
const ensure = (): Promise<void> => {
  arrival ??= import("@adea/platform/staff/specs").then((m) => {
    Object.assign(loaded, m.staffSpecs);
  });
  return arrival;
};

export const staffActions: ActionClient<StaffSpecs> = lazyClient<StaffSpecs>(
  { loaded: (name) => loaded[name], ensure },
  {
    fetch: staffFetch,
    headers: (): Record<string, string> => {
      const token = getStaffCsrf();
      return token ? { "x-adea-staff-csrf": token } : {};
    },
    locale: callLocale,
  },
  confirmWith,
);
